//@ts-check
/**
 * Per-model and global OPFS cache client for RCS point clouds.
 *
 * Owns worker dispatch, session lifecycle (INIT/CLOSE), header/octree
 * bootstrap I/O, aggregate cache statistics, and the registry of active
 * {@link RcsFileLoader} instances. Used by {@link RcsFileLoader} and
 * {@link LoadRcsNodeTask}.
 *
 * v1 OPFS targets warm reload and interrupted cold loads (header/octree,
 * inner nodes, transient raw LOD-0 SVO bytes, and retained LOD-0 leaf overflow
 * points). Inner BVH nodes are not unloaded mid-session yet, this will be added later.
 */

import { rcsWorkerPool } from "./RcsWorkerPool";
import { RcsOpfsCacheStats } from "./RcsOpfsCacheStats.js";
import { digest } from '../lmvtk/otg/HashStrings';
import { RCS_FLOATS_PER_POINT } from './RcsConstants.js';
import {
  RCS_OPFS_CACHE_DIR,
  getDataBucketName, getLod0BucketName,
  paramsHashKey, levelsInLod0HashKey, headerHashKey, octreeHashKey,
  innerNodeHashKey, leafOverflowHashKey,
  validateHeaderBytes, validateOctreeChunkBytes,
  serializeCacheParams, serializeLevelsInLod0, deserializeLevelsInLod0,
  serializePointData, deserializePointData, drainPendingPromises } from
'./RcsOpfsCache.js';

/** @import { RcsOpfsJobResponse } from './RcsWorkerPool' */
/** @import { RcsFileLoader } from './RcsFileLoader' */
/** @import { ParentLodRange, RcsPackage } from './RcsPackage' */
/** @import { Model } from '../../application/Model' */

/**
 * One blob buffered in a batched OPFS operation.
 * @template T
 * @typedef {Object} RcsOpfsBatchItem
 * @property {string} hashKey - Logical cache key
 * @property {string} bucketName - OPFS bucket the key lives in
 * @property {(value: T) => void} resolve - Settles the caller's promise with the operation result
 * @property {(error: unknown) => void} reject - Rejects the caller's promise
 */

/** @typedef {RcsOpfsBatchItem<boolean> & { data: Uint8Array, transferables?: Transferable[] }} RcsOpfsStoreItem */

/** @typedef {RcsOpfsBatchItem<Uint8Array|null>} RcsOpfsGetItem */

/**
 * Upper bound for {@link RcsOpfsCacheClient.waitForOpfsTeardown}. Each teardown ends in a CLOSE
 * round-trip, so allow more than the per-job drain budget it already contains.
 */
const OPFS_TEARDOWN_TIMEOUT_MS = 15000;

export class RcsOpfsCacheClient {

  /**
   * When false, {@link init} is a no-op and no OPFS I/O is attempted, so every load goes to the
   * network. Set from the `ENABLE_RCS_OPFS_CACHE` feature flag at viewer initialization, and
   * toggled off for the duration of a {@link clearOpfsCache}.
   */
  static opfsCacheEnabled = false;

  /** In-flight {@link clearOpfsCache} promise; serializes overlapping clears. @type {Promise<void>|null} */
  static _clearPromise = null;

  /**
   * Monotonic source of unique worker-side session tokens. Each {@link init} that
   * dispatches `RCS_OPFS_INIT` claims a fresh token and passes the same token to the
   * matching `RCS_OPFS_CLOSE`, so the worker can ref-count sessions by an idempotent
   * token set rather than a bare counter. Never reused within a page lifetime.
   * @type {number}
   */
  static _nextSessionToken = 1;

  /**
   * Promises of in-flight async teardowns (from loader `dtor()`).
   * @type {Set<Promise<void>>}
   */
  static _pendingTeardowns = new Set();

  /**
   * Open {@link RcsFileLoader} instances (registered in the constructor,
   * removed in {@link RcsFileLoader.dtor}). Not cleared by
   * {@link clearOpfsCache} — that only disables OPFS on each instance.
   * @type {Set<RcsFileLoader>}
   */
  static _activeLoaders = new Set();

  /**
   * Register a loader that owns this client's worker-pool slot.
   * @param {RcsFileLoader} loader - Loader instance to track for {@link clearOpfsCache} and teardown
   */
  static registerActiveLoader(loader) {
    RcsOpfsCacheClient._activeLoaders.add(loader);
  }

  /**
   * Unregister a loader when it is torn down.
   * @param {RcsFileLoader} loader - Loader instance being destroyed
   */
  static unregisterActiveLoader(loader) {
    RcsOpfsCacheClient._activeLoaders.delete(loader);
  }

  /** @type {{ items: RcsOpfsStoreItem[] } | null} */
  static _pendingStoreBatch = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  static _storeBatchTimer = null;
  static _STORE_BATCH_MS = 16;
  /**
   * Latest buffered store per `${bucketName}\u0000${hashKey}`, so a GET issued while a
   * store for the same key is still debounced reads the pending write instead of racing
   * it to the worker (which would miss and trigger a redundant fetch + duplicate append).
   * Kept in sync with {@link _pendingStoreBatch}; cleared when the batch is flushed.
   * @type {Map<string, Uint8Array>}
   */
  static _pendingStoreIndex = new Map();

  /** @type {{ items: RcsOpfsGetItem[] } | null} */
  static _pendingGetBatch = null;

  /**
   * @param {string} hashKey
   * @param {string} bucketName
   * @param {Uint8Array} data
   * @param {Transferable[]} [transferables]
   * @returns {Promise<boolean>}
   */
  static _enqueueStore(hashKey, bucketName, data, transferables) {
    return new Promise((resolve, reject) => {
      if (!RcsOpfsCacheClient._pendingStoreBatch) {
        RcsOpfsCacheClient._pendingStoreBatch = { items: [] };
      }
      const indexKey = `${bucketName}\u0000${hashKey}`;
      // OPFSCache.store appends unconditionally and only overwrites its in-memory index, so
      // two buffered writes of the same key would leave one copy stranded on disk forever.
      // Replace the earlier item's payload instead and settle both callers from one write.
      const buffered = RcsOpfsCacheClient._pendingStoreIndex.has(indexKey) ?
      RcsOpfsCacheClient._pendingStoreBatch.items.find(
        (item) => item.hashKey === hashKey && item.bucketName === bucketName) :
      undefined;
      if (buffered) {
        const previousResolve = buffered.resolve;
        const previousReject = buffered.reject;
        buffered.data = data;
        buffered.transferables = transferables;
        buffered.resolve = (value) => {previousResolve(value);resolve(value);};
        buffered.reject = (error) => {previousReject(error);reject(error);};
        RcsOpfsCacheClient._pendingStoreIndex.set(indexKey, data);
        return;
      }
      RcsOpfsCacheClient._pendingStoreBatch.items.push({
        hashKey, bucketName, data, transferables, resolve, reject
      });
      RcsOpfsCacheClient._pendingStoreIndex.set(indexKey, data);
      if (!RcsOpfsCacheClient._storeBatchTimer) {
        RcsOpfsCacheClient._storeBatchTimer = setTimeout(
          () => void RcsOpfsCacheClient.flushPendingStores(),
          RcsOpfsCacheClient._STORE_BATCH_MS
        );
      }
    });
  }

  /**
   * Send the debounced STORE batch to the worker. Also called directly before worker-side
   * drains (clear, teardown, bucket delete) to make buffered writes visible there.
   * @returns {Promise<void>}
   */
  static async flushPendingStores() {
    if (RcsOpfsCacheClient._storeBatchTimer) {
      clearTimeout(RcsOpfsCacheClient._storeBatchTimer);
      RcsOpfsCacheClient._storeBatchTimer = null;
    }
    const batch = RcsOpfsCacheClient._pendingStoreBatch;
    RcsOpfsCacheClient._pendingStoreBatch = null;
    RcsOpfsCacheClient._pendingStoreIndex.clear();
    if (!batch?.items.length) {
      return;
    }

    const hashKeys = batch.items.map((item) => item.hashKey);
    const bucketNames = batch.items.map((item) => item.bucketName);
    const datas = batch.items.map((item) => item.data);
    const transferables = batch.items.flatMap((item) => item.transferables ?? []);

    try {
      const result = await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_STORE', { hashKeys, bucketNames, datas }, transferables);
      const success = result?.result?.success ?? false;
      batch.items.forEach((item) => item.resolve(success));
    } catch (e) {
      batch.items.forEach((item) => item.reject(e));
    }
  }

  /**
   * @param {string} hashKey
   * @param {string} bucketName
   * @returns {Promise<Uint8Array|null>}
   */
  static _enqueueGet(hashKey, bucketName) {
    // Read-after-write: if a store for the same key/bucket is still buffered, resolve from
    // it directly. Return a copy so a later transfer of the store's buffer to the worker
    // cannot neuter the data handed to the reader.
    const pendingData = RcsOpfsCacheClient._pendingStoreIndex.get(`${bucketName}\u0000${hashKey}`);
    if (pendingData) {
      return Promise.resolve(pendingData.slice());
    }
    return new Promise((resolve, reject) => {
      if (!RcsOpfsCacheClient._pendingGetBatch) {
        RcsOpfsCacheClient._pendingGetBatch = { items: [] };
        queueMicrotask(() => void RcsOpfsCacheClient._flushPendingGetBatch());
      }
      RcsOpfsCacheClient._pendingGetBatch.items.push({ hashKey, bucketName, resolve, reject });
    });
  }

  /** @returns {Promise<void>} */
  static async _flushPendingGetBatch() {
    const batch = RcsOpfsCacheClient._pendingGetBatch;
    RcsOpfsCacheClient._pendingGetBatch = null;
    if (!batch?.items.length) {
      return;
    }

    const hashKeys = batch.items.map((item) => item.hashKey);
    const bucketNames = batch.items.map((item) => item.bucketName);

    try {
      const result = await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_GET', { hashKeys, bucketNames });
      const datas = result?.result?.datas ?? [];
      batch.items.forEach((item, index) => item.resolve(datas[index] ?? null));
    } catch (e) {
      batch.items.forEach((item) => item.reject(e));
    }
  }

  /**
   * Returns a Promise that resolves once every pending OPFS teardown has settled.
   * @param {number} [timeoutMs] - Give up after this long. A teardown ends in a CLOSE dispatch,
   * which never settles if the worker stops replying; a cache clear must not hang on that.
   * @returns {Promise<boolean>} True if all teardowns settled, false if the timeout was hit.
   */
  static waitForOpfsTeardown(timeoutMs = OPFS_TEARDOWN_TIMEOUT_MS) {
    return drainPendingPromises(() => RcsOpfsCacheClient._pendingTeardowns, timeoutMs);
  }

  /**
   * Clear all RCS OPFS cache directories.
   *
   * Open models are not required to unload first: each active loader's OPFS
   * session is disabled (network-only from then on), in-flight jobs are drained,
   * and the fetch worker clears its {@link OPFSCache} via {@link OPFSCache.clear}
   * (same eviction path as OTG). New model loads after this call may populate a
   * fresh cache; already-open models keep caching off until they are reloaded.
   *
   * If no point cloud is currently loaded (no fetch worker running), a temporary
   * worker is spun up so the on-disk cache can still be cleared, then torn down.
   *
   * Concurrent calls share a single in-flight clear so overlapping invocations
   * cannot corrupt the saved {@link opfsCacheEnabled} state.
   *
   * @returns {Promise<void>}
   */
  static clearOpfsCache() {
    if (RcsOpfsCacheClient._clearPromise) {
      return RcsOpfsCacheClient._clearPromise;
    }
    const promise = RcsOpfsCacheClient._clearOpfsCacheImpl();
    RcsOpfsCacheClient._clearPromise = promise;
    return promise.finally(() => {RcsOpfsCacheClient._clearPromise = null;});
  }

  /**
   * Implementation of {@link clearOpfsCache}; serialized via `_clearPromise`.
   * @returns {Promise<void>}
   */
  static async _clearOpfsCacheImpl() {
    const previousEnabled = RcsOpfsCacheClient.opfsCacheEnabled;
    RcsOpfsCacheClient.opfsCacheEnabled = false;

    try {
      for (const loader of RcsOpfsCacheClient._activeLoaders) {
        loader.opfsCache.disableForCacheClear();
      }

      // Disable first so loaders stop dispatching; then drain in-flight jobs. A drain that
      // times out still proceeds to CLEAR: eviction wipes the directory regardless, and
      // leaving the cache in place because one job is stuck is the worse outcome.
      await RcsOpfsCacheClient.flushPendingStores();
      const drained = await rcsWorkerPool.waitForPendingOpfsJobs();
      const tornDown = await RcsOpfsCacheClient.waitForOpfsTeardown();
      if (!drained || !tornDown) {
        console.warn('RCS OPFS jobs did not drain before clear; clearing anyway');
      }
    } catch (e) {
      console.warn('Failed to clear RCS OPFS cache:', e);
      RcsOpfsCacheClient.opfsCacheEnabled = previousEnabled;
      return;
    }

    // Hold our own worker-pool ref-count for the whole CLEAR round-trip. register()
    // starts the fetch worker if none is running (e.g. no point cloud loaded) and, when
    // a loader is already running, keeps the worker alive even if that loader tears down
    // mid-clear. Without this the last loader's dtor could terminate the worker during the
    // await below, rejecting CLEAR and silently leaving the on-disk cache in place.
    //
    // register() is inside the try so a failure to start the worker still restores
    // opfsCacheEnabled; otherwise caching would stay off for the rest of the page.
    let registered = false;
    try {
      rcsWorkerPool.register();
      registered = true;
      await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_CLEAR', { cacheDir: RCS_OPFS_CACHE_DIR });
      const activeCount = RcsOpfsCacheClient._activeLoaders.size;
      if (activeCount > 0) {
        console.log(
          `RCS OPFS cache cleared (${activeCount} open model(s) continue without caching)`
        );
      } else {
        console.log('RCS OPFS cache cleared');
      }
    } catch (e) {
      console.warn('Failed to clear RCS OPFS cache:', e);
    } finally {
      if (registered) {
        rcsWorkerPool.unregister();
      }
      RcsOpfsCacheClient.opfsCacheEnabled = previousEnabled;
    }
  }

  /**
   * Derive a stable OPFS cache key from a URL or path (hex SHA-1).
   *
   * The identifier must be stable across sessions for the same model. Production loads pass a
   * URN; the URL fallback is for direct `.rcs` loads, where the caller passes the load URL as
   * given rather than the signed download URL derived from it, so a rotating S3 signature does
   * not end up in the key. Feeding a pre-signed URL in directly would defeat the cache: every
   * session would hash to a fresh key and leave its own copy behind until eviction.
   *
   * @param {string} identifier - Model URL, Forge URN, or other stable source identifier
   * @returns {Promise<string>} Hex SHA-1 digest used as the OPFS session key
   */
  static async cacheKeyFromIdentifier(identifier) {
    return /** @type {string} */await digest('SHA-1', identifier, true);
  }

  /**
   * Create a per-loader OPFS session tracker. One instance is owned by each
   * {@link RcsFileLoader} and drives INIT/CLOSE pairing on the shared worker.
   */
  constructor() {
    /** @type {string|null} OPFS cache namespace (Forge URN or URL-derived id). */
    this.cacheId = null;
    this.ready = false;
    this.initSent = false;
    this.sessionOpen = false;
    this.closeSent = false;
    /**
     * Unique worker-side session token, claimed just before `RCS_OPFS_INIT` is
     * dispatched and echoed in the matching `RCS_OPFS_CLOSE`. Null until an INIT is
     * actually sent, so a CLOSE for a never-initialized session carries a null token
     * that the worker's session set simply ignores.
     * @type {number|null}
     */
    this._sessionToken = null;
    /**
     * When true, this loader must never read or write OPFS until it is
     * torn down (set by {@link disableForCacheClear}).
     */
    this.cachingDisabled = false;
    this._allInnerNodesCachedDone = false;
    /** @type {Array<() => void>} */
    this._allInnerNodesCachedWaiters = [];

    /**
     * Per inner BVH node id: how many umbrella leaves contribute HLOD points (`expected`)
     * and how many have loaded (`loaded`). Populated by {@link initInnerNodeCompletion}.
     * @type {Map<number, {expected: number, loaded: number}>}
     */
    this.innerNodeCompletion = new Map();
    /**
     * Inner nodes persisted to or loaded from OPFS. Always a subset of
     * {@link innerNodeCompletion}'s keys, which is what lets
     * {@link checkAndSignalInnerNodeCompletion} compare sizes instead of membership.
     * @type {Set<number>}
     */
    this.innerNodesCachedSet = new Set();
    /** Inner nodes whose OPFS store failed; not retried within this session. @type {Set<number>} */
    this.innerNodesCacheGiveUpSet = new Set();
    /** Umbrella-leaf indices already counted by {@link trackInnerNodeCompletion}. @type {Set<number>} */
    this._completionCountedLeaves = new Set();
    /**
     * Umbrella-leaf indices whose overflow points are already persisted this session.
     * LOD-0 leaves can be demoted and re-promoted under memory heavy pressure (if there
     * are only LOD0 nodes in the cache and all nodes get the same 1e5 boost), and each
     * re-promotion re-runs the decode; without this the same blob would be appended again
     * on every cycle, since {@link OPFSCache.store} never overwrites in place.
     * @type {Set<number>}
     */
    this._leafOverflowStored = new Set();
    /**
     * True once the LOD-0 bucket delete has been dispatched for this model, so
     * repeated completion signals don't enqueue redundant delete jobs.
     */
    this._lod0BucketDeleteRequested = false;
    /**
     * Highest LOD level whose raw SVO fragments may be written to the per-file OPFS cache.
     * `Infinity` when unlimited. Decided once at model open from the user's cache-size preference.
     * @type {number}
     */
    this.maxCachedLodLevel = Infinity;
  }

  /**
   * Whether all inner nodes for this model have been cached to OPFS.
   * @returns {boolean}
   */
  get allInnerNodesCachedDone() {
    return this._allInnerNodesCachedDone;
  }

  /**
   * Test-only: returns a Promise that resolves when all inner nodes of this model
   * have been cached to OPFS. Resolves immediately if already complete.
   * @returns {Promise<void>}
   */
  async _waitForInnerNodesCached() {
    if (this._allInnerNodesCachedDone) return;
    await new Promise((resolve) => {
      this._allInnerNodesCachedWaiters.push(() => resolve(undefined));
    });
  }

  /**
   * Signal that all inner nodes for this loader have been cached.
   * Idempotent — safe to call multiple times.
   */
  signalAllInnerNodesCached() {
    if (this._allInnerNodesCachedDone) return;
    this._allInnerNodesCachedDone = true;
    for (const resolve of this._allInnerNodesCachedWaiters) resolve();
    this._allInnerNodesCachedWaiters = [];
  }

  /**
   * Resolve any pending {@link _waitForInnerNodesCached} waiters immediately
   * (drain without signalling "done"). Called from loader `dtor()` so callers
   * that were waiting don't hang indefinitely after model unload.
   */
  drainInnerNodesCachedWaiters() {
    for (const resolve of this._allInnerNodesCachedWaiters) resolve();
    this._allInnerNodesCachedWaiters = [];
  }

  /**
   * Walk all umbrella leaves and count how many contribute HLOD points to each inner
   * BVH node. Called once after {@link RcsPackage} construction.
   * @param {RcsPackage} pkg - Built package whose BVH parent LOD ranges drive completion tracking
   */
  initInnerNodeCompletion(pkg) {
    this.innerNodeCompletion.clear();
    this.innerNodesCachedSet.clear();
    this.innerNodesCacheGiveUpSet.clear();
    this._completionCountedLeaves.clear();
    this._leafOverflowStored.clear();
    this._allInnerNodesCachedDone = false;
    this._lod0BucketDeleteRequested = false;

    // Nothing consumes the completion tracking when OPFS is off, and this walk is
    // O(leaves x parent ranges) with a temporary Set per leaf. Leaving the map empty makes
    // RcsFileLoader signal completion immediately, and an empty innerNodesCachedSet keeps
    // the allParentsCached short-circuit in LoadRcsNodeTask correctly disabled.
    if (!this._isOpfsIoAllowed()) {
      return;
    }

    for (let leafBvhId = 0, nodeCount = pkg.bvh?.nodes.nodeCount ?? 0; leafBvhId < nodeCount; leafBvhId++) {
      const ranges = pkg.getNodeParentLodRanges(leafBvhId);
      if (!ranges) continue;
      const parentBvhNodeIds = new Set(ranges.map((r) => r.bvhNodeId));
      for (const bvhNodeId of parentBvhNodeIds) {
        // Every target listed in a leaf's parent LOD ranges receives pulled-up points.
        if (!pkg.isInnerNode(bvhNodeId)) continue;
        let tracker = this.innerNodeCompletion.get(bvhNodeId);
        if (!tracker) {
          tracker = { expected: 0, loaded: 0 };
          this.innerNodeCompletion.set(bvhNodeId, tracker);
        }
        tracker.expected++;
      }
    }
  }

  /**
   * Stop using OPFS for this loader while the model stays open (e.g. after
   * {@link RcsOpfsCacheClient.clearOpfsCache}). Further cache reads/writes
   * are skipped; node loading continues over the network.
   *
   * Does not send {@code RCS_OPFS_CLOSE}; {@link clearOpfsCache} uses
   * {@code RCS_OPFS_CLEAR} to close and evict the worker cache in one step.
   * {@code closeSent} prevents a later {@link releaseSession} from sending CLOSE.
   */
  disableForCacheClear() {
    this.cachingDisabled = true;
    this.ready = false;
    this.sessionOpen = false;
    this.closeSent = true;
    this.innerNodesCachedSet.clear();
    this.innerNodesCacheGiveUpSet.clear();
    this._leafOverflowStored.clear();
    this._allInnerNodesCachedDone = false;
    this.drainInnerNodesCachedWaiters();
  }

  /**
   * Initialize the worker-side OPFS session for this model.
   *
   * Unlike OTG, which opens OPFS buckets lazily and checks cache per resource batch,
   * RCS blocks on INIT before bootstrap. A warm cache can supply header, octree, and
   * inner-node geometry up front, skipping a large number of network fetches that OTG
   * would still issue on cache miss. The caller needs a definitive {@code hasCache}
   * answer before reading the RCS header and umbrella octree.
   *
   * INIT is capped at 15 seconds. On timeout the load continues network-only for this
   * session ({@code ready = false}); late worker success is not recovered, since
   * successful OPFS open after that delay is assumed to be rare.
   *
   * @param {Object} options
   * @param {string|null} options.urn - Forge URN when loading via `rcs:` URL; null for direct URL loads
   * @param {string} options.url - Model source URL; used to derive {@link cacheId} when `urn` is null
   * @param {string} options.cacheDir - OPFS root directory name
   * @returns {Promise<boolean>} `true` when cached params match and header/octree may be loaded from OPFS
   */
  async init({ urn, url, cacheDir }) {
    if (this.cachingDisabled || !RcsOpfsCacheClient.opfsCacheEnabled) {
      this.ready = false;
      return false;
    }

    this.cacheId = urn ?? (await RcsOpfsCacheClient.cacheKeyFromIdentifier(url));

    // Claim a unique session token that pairs this INIT with its later CLOSE. Only
    // assigned when we actually dispatch INIT, so the worker never sees a spurious
    // CLOSE token for a session it never opened.
    this._sessionToken = RcsOpfsCacheClient._nextSessionToken++;
    // Marks that an INIT was dispatched to the worker; dtor() uses this to decide whether
    // a session teardown (CLOSE) is needed. Set only after the opfsCacheEnabled guard so a
    // no-op init (caching disabled) does not trigger a spurious CLOSE for a session that
    // was never opened.
    this.initSent = true;

    /** @type {ReturnType<typeof setTimeout>|undefined} */
    let timeoutId;
    try {
      const initPromise = rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_INIT', {
        urn: this.cacheId,
        cacheDir,
        token: this._sessionToken,
        // The worker validates the cached params against these instead of its own
        // module state, which does not see main-thread parameter overrides.
        params: serializeCacheParams()
      });
      // Bound bootstrap wait; see method JSDoc for why INIT is blocking and why we do
      // not recover if the worker finishes successfully after this deadline.
      const timeoutPromise = new Promise((resolve) => {
        timeoutId = setTimeout(() => resolve({ result: { hasCache: false, error: 'timeout' } }), 15000);
      });
      const initResult = /** @type {RcsOpfsJobResponse} */await Promise.race([initPromise, timeoutPromise]);
      const error = initResult.result?.error;

      // A concurrent clearOpfsCache() may have run disableForCacheClear() during the await.
      // Respect it: do not resurrect ready/sessionOpen (reads gate on `ready`), so this
      // loader stays network-only. The worker-side session token was already dropped by
      // RCS_OPFS_CLEAR (which waits for this in-flight INIT first), so nothing leaks.
      if (this.cachingDisabled) {
        this.ready = false;
        this.sessionOpen = false;
        return false;
      }

      // Success: the worker opened the session, so this loader may use OPFS.
      if (!error) {
        this.sessionOpen = true;
        this.ready = true;
        return initResult.result?.hasCache ?? false;
      }

      this.ready = false;
      if (error === 'timeout') {
        // Tear down the worker session once the late INIT settles; this load stays
        // network-only. then(fn, fn) rather than finally(): finally() returns a new
        // promise that re-propagates rejection with no .catch(), which can become an
        // unhandled rejection.
        const release = () => {void this.releaseSession();};
        initPromise.then(release, release);
      }
      return false;
    } catch (e) {
      console.warn("OPFS cache init failed:", e);
      this.ready = false;
      await this.releaseSession();
      return false;
    } finally {
      // The race is settled; drop the timeout timer so it does not fire later and keep
      // its closure (and any test runner) alive for the full 15 s.
      clearTimeout(timeoutId);
    }
  }

  /**
   * Send RCS_OPFS_CLOSE once for this session (idempotent).
   * @returns {Promise<void>}
   */
  async releaseSession() {
    if (this.closeSent) return;
    this.closeSent = true;
    this.sessionOpen = false;
    this.ready = false;
    try {
      await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_CLOSE', { token: this._sessionToken });
    } catch (e) {
      console.warn('RCS OPFS close failed during session release:', e);
    }
  }

  /**
   * Wait for pending store jobs, close the OPFS session, and unregister the worker pool slot.
   * @returns {Promise<void>}
   */
  async teardownBeforeUnregister() {
    try {
      await RcsOpfsCacheClient.flushPendingStores();
      if (!(await rcsWorkerPool.waitForPendingOpfsJobs())) {
        // Proceed anyway: the session must be released and the pool ref-count dropped even
        // if a job is stuck, otherwise the workers stay alive for the rest of the page.
        console.warn('RCS OPFS jobs did not drain before teardown; closing the session anyway');
      }
      await this.releaseSession();
    } catch (e) {
      console.warn('RCS OPFS teardown before unregister failed:', e);
    }
    rcsWorkerPool.unregister();
  }

  /**
   * Schedule async OPFS teardown; the returned promise is tracked in {@link _pendingTeardowns}.
   * @returns {Promise<void>}
   */
  scheduleTeardown() {
    const promise = this.teardownBeforeUnregister();
    RcsOpfsCacheClient._pendingTeardowns.add(promise);
    const cleanup = () => {RcsOpfsCacheClient._pendingTeardowns.delete(promise);};
    promise.then(cleanup, cleanup);
    return promise;
  }

  /**
   * @returns {boolean} True when this session may perform OPFS reads or writes.
   *
   * `cachingDisabled` is redundant with `ready` today because {@link disableForCacheClear} clears
   * both, but every gate goes through this one predicate so the two can never drift apart.
   */
  _isOpfsIoAllowed() {
    return this.ready && !!this.cacheId && !this.cachingDisabled;
  }

  /**
   * Whether raw SVO fragments at the given LOD level may use the OPFS cache.
   * LOD 0 tier (inner nodes, leaf overflow, transient raw bucket) is not gated here.
   *
   * @param {number} lodLevel - Target LOD level index
   * @returns {boolean}
   */
  isLodLevelCacheable(lodLevel) {
    return this._isOpfsIoAllowed() && lodLevel <= this.maxCachedLodLevel;
  }

  /**
   * Write one blob to OPFS.
   * @param {string} hashKey - Logical cache key from one of the key builders in `RcsOpfsCache.js`
   * @param {string} bucketName - OPFS bucket name from {@link getDataBucketName} or {@link getLod0BucketName}
   * @param {Uint8Array} data - Serialized blob to persist
   * @param {Transferable[]} [transferables] - ArrayBuffers to transfer zero-copy to the worker
   * @param {{ immediate?: boolean }} [options] - `immediate` dispatches its own single-blob
   *   STORE and awaits a definitive success result (used for inner nodes, whose completion
   *   tracking needs to know whether the write actually landed)
   * @returns {Promise<boolean>}
   */
  async _storeCachedBlob(hashKey, bucketName, data, transferables, options = {}) {
    if (!this._isOpfsIoAllowed()) {
      return false;
    }
    const immediate = options.immediate ?? false;
    if (immediate) {
      // Dispatch as a standalone STORE without draining the debounced batch. Immediate
      // keys (inner nodes) never collide with the batched fire-and-forget leaf/LOD-0 keys,
      // and per-entry appends are order-independent, so force-flushing the batch here would
      // only fragment it into many tiny worker jobs during the inner-node completion burst.
      const result = await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_STORE', {
        hashKeys: [hashKey],
        bucketNames: [bucketName],
        datas: [data]
      }, transferables);
      return result?.result?.success ?? false;
    }
    try {
      return await RcsOpfsCacheClient._enqueueStore(hashKey, bucketName, data, transferables);
    } catch {
      return false;
    }
  }

  /**
   * Load interleaved point data from OPFS with shared validation and cache stats.
   * @param {string} hashKey - Logical cache key
   * @param {string} bucketName - OPFS bucket name
   * @param {number} [expectedPointCount] - When set, reject cached data whose float count does
   *   not equal `expectedPointCount * RCS_FLOATS_PER_POINT`. Omit to accept any buffer.
   * @returns {Promise<Float32Array|null>}
   */
  async _loadPointData(hashKey, bucketName, expectedPointCount) {
    if (!this._isOpfsIoAllowed()) return null;
    try {
      const data = await RcsOpfsCacheClient._enqueueGet(hashKey, bucketName);
      if (!data) {
        RcsOpfsCacheStats.cacheMisses++;
        return null;
      }
      const vb = deserializePointData(data);
      if (expectedPointCount !== undefined && vb.length !== expectedPointCount * RCS_FLOATS_PER_POINT) {
        RcsOpfsCacheStats.cacheMisses++;
        return null;
      }
      RcsOpfsCacheStats.cacheHits++;
      RcsOpfsCacheStats.cacheBytesLoaded += vb.byteLength;
      return vb;
    } catch {
      RcsOpfsCacheStats.cacheMisses++;
      return null;
    }
  }

  /**
   * Store an already serialized blob without blocking the caller (fire-and-forget).
   * @param {string} hashKey - Logical cache key
   * @param {string} bucketName - OPFS bucket name
   * @param {Uint8Array} data - Serialized blob to persist
   * @param {string} warnMessage - Message passed to {@link console.warn} on failure
   */
  _storeBlobFireAndForget(hashKey, bucketName, data, warnMessage) {
    if (!this._isOpfsIoAllowed()) return;
    void RcsOpfsCacheClient._enqueueStore(hashKey, bucketName, data, [data.buffer]).
    catch((e) => console.warn(warnMessage, e));
  }

  /**
   * Load an inner node's interleaved point buffer from OPFS.
   * @param {number} bvhNodeId - Inner BVH node id (cache key)
   * @param {number} [pointCount] - When set, reject cached data whose float count does not match `pointCount * RCS_FLOATS_PER_POINT`
   * @returns {Promise<Float32Array|null>}
   */
  async loadInnerNodePoints(bvhNodeId, pointCount) {
    // Checked before deriving the bucket name, which requires a cacheId this session may not have.
    if (!this._isOpfsIoAllowed()) return null;
    return this._loadPointData(
      innerNodeHashKey(this.cacheId, bvhNodeId),
      getDataBucketName(this.cacheId),
      pointCount
    );
  }

  /**
   * Load a LOD-0 umbrella leaf's decoded fall-through (overflow) points from the
   * retained data bucket. These are the points that spilled back onto the leaf itself
   * during the HLOD pull-up (see {@link leafOverflowHashKey}); unlike the transient
   * raw LOD-0 bytes, they survive so warm reloads can render the leaf after all inner
   * nodes are cached.
   * @param {number} umbrellaIndex - Index into the umbrella leaf array
   * @param {number} pointCount - Expected leaf overflow point count; a mismatched blob is rejected
   * @returns {Promise<Float32Array|null>}
   */
  async loadLeafOverflowPoints(umbrellaIndex, pointCount) {
    // Checked before deriving the bucket name, which requires a cacheId this session may not have.
    if (!this._isOpfsIoAllowed()) return null;
    return this._loadPointData(
      leafOverflowHashKey(this.cacheId, umbrellaIndex),
      getDataBucketName(this.cacheId),
      pointCount
    );
  }

  /**
   * Store a LOD-0 umbrella leaf's decoded fall-through (overflow) points to the retained
   * data bucket (fire-and-forget).
   * @param {number} umbrellaIndex - Index into the umbrella leaf array
   * @param {Float32Array} overflowPoints - Interleaved XYZRGB overflow vertex buffer
   */
  storeLeafOverflowPoints(umbrellaIndex, overflowPoints) {
    // Check eligibility before serializing: serializePointData copies the whole vertex buffer,
    // which is pure waste when the blob is already stored or OPFS is off entirely.
    if (!this._isOpfsIoAllowed() || this._leafOverflowStored.has(umbrellaIndex)) return;
    this._leafOverflowStored.add(umbrellaIndex);
    const data = serializePointData(overflowPoints);
    this._storeBlobFireAndForget(
      leafOverflowHashKey(this.cacheId, umbrellaIndex),
      getDataBucketName(this.cacheId),
      data,
      'Failed to store RCS LOD-0 leaf overflow points to OPFS cache:'
    );
  }

  /**
   * Record that an inner node's cache obligation is satisfied.
   * @param {number} bvhNodeId - Inner BVH node id whose OPFS entry is complete
   */
  markInnerNodeCached(bvhNodeId) {
    // Only tracked nodes may enter innerNodesCachedSet, otherwise the size comparison in
    // checkAndSignalInnerNodeCompletion would fire early. Callers do not check this
    // themselves: LoadRcsNodeTask only caches inner nodes with pulled-up points, and every
    // such node is a target in some leaf's parentLodRanges, hence tracked by
    // initInnerNodeCompletion.
    console.assert(this.innerNodeCompletion.has(bvhNodeId),
    `RCS OPFS: inner node ${bvhNodeId} cached but not tracked for completion`);

    // The two sets must stay disjoint: a node that was given up earlier and cached later
    // must no longer count as given up.
    this.innerNodesCacheGiveUpSet.delete(bvhNodeId);
    this.innerNodesCachedSet.add(bvhNodeId);
    this.checkAndSignalInnerNodeCompletion();
  }

  /**
   * Mark an inner node as permanently skipped for OPFS persistence.
   * @param {number} bvhNodeId - Inner BVH node id that will not be stored to OPFS
   */
  giveUpInnerNodeCache(bvhNodeId) {
    // The two sets must stay disjoint, see markInnerNodeCached.
    this.innerNodesCachedSet.delete(bvhNodeId);
    this.innerNodesCacheGiveUpSet.add(bvhNodeId);
  }

  /**
   * Signal completion once every inner node has been written to OPFS.
   *
   * Nodes we gave up on do not count: giving up means the write failed, so deleting the
   * LOD-0 bucket would force a full network reload of those nodes. Keeping the bucket
   * lets the next session complete the cache instead.
   */
  checkAndSignalInnerNodeCompletion() {
    // Comparing sizes is equivalent to checking that every tracked node is cached, because
    // innerNodesCachedSet only ever holds ids that are keys in innerNodeCompletion (asserted
    // in markInnerNodeCached). An untracked id would inflate the size and delete the LOD-0
    // bucket before every tracked node is persisted.
    if (this.innerNodeCompletion.size === 0 ||
    this.innerNodesCachedSet.size < this.innerNodeCompletion.size) {
      return;
    }
    this.signalAllInnerNodesCached();
    // Keep the transient LOD-0 bucket (raw SVO bytes) until every inner node is
    // persisted. After an interrupted cold load, some leaves may have cached their
    // raw LOD-0 bytes while their parent inner nodes were never written to the data
    // bucket; on reload those bytes avoid a network re-fetch until inner-node caching
    // catches up. Once all inner nodes are complete, warm reloads render inner-node
    // geometry plus the retained leaf-overflow points (allParentsCached), so the raw
    // LOD-0 bucket can be deleted to reclaim space.
    //
    // Completion can be re-signalled (e.g. an inner node re-cached after a reload of a
    // demoted node), so guard against enqueuing redundant delete jobs.
    if (!this._lod0BucketDeleteRequested && this._isOpfsIoAllowed()) {
      this._lod0BucketDeleteRequested = true;
      void this._deleteBucket(getLod0BucketName(this.cacheId), 'Failed to delete RCS LOD-0 OPFS bucket:');
    }
  }

  /**
   * Drain pending stores, then delete an OPFS bucket. Callers apply their own
   * eligibility guard before invoking.
   * @param {string} bucketName - OPFS bucket to remove
   * @param {string} warnMessage - Message passed to {@link console.warn} on failure
   * @returns {Promise<void>}
   */
  async _deleteBucket(bucketName, warnMessage) {
    try {
      await RcsOpfsCacheClient.flushPendingStores();
      // A timeout is not fatal here: the worker retires the bucket before its own drain, so
      // a store that is still queued is dropped rather than recreating the deleted files.
      await rcsWorkerPool.waitForPendingOpfsJobs();
      await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_DELETE_BUCKET', { bucketName });
    } catch (e) {
      console.warn(warnMessage, e);
    }
  }

  /**
   * Track inner node completion and cache when all contributors loaded.
   * @param {RcsFileLoader} rcsLoader - Loader owning the model geometry to read from
   * @param {ParentLodRange[]} parentLodRanges - HLOD distribution targets for the contributing leaf
   * @param {number} umbrellaNodeIndex - Umbrella leaf that just finished HLOD pull-up
   */
  trackInnerNodeCompletion(rcsLoader, parentLodRanges, umbrellaNodeIndex) {
    if (!this._isOpfsIoAllowed()) return;

    if (this._completionCountedLeaves.has(umbrellaNodeIndex)) return;
    this._completionCountedLeaves.add(umbrellaNodeIndex);

    const parentBvhNodeIds = new Set(parentLodRanges?.map((r) => r.bvhNodeId) ?? []);

    for (const bvhNodeId of parentBvhNodeIds) {
      const tracker = this.innerNodeCompletion.get(bvhNodeId);
      if (!tracker) continue;
      tracker.loaded++;

      if (tracker.loaded >= tracker.expected &&
      this._isOpfsIoAllowed() &&
      !this.innerNodesCachedSet.has(bvhNodeId) &&
      !this.innerNodesCacheGiveUpSet.has(bvhNodeId)) {
        void this.storeInnerNodeFromLoader(rcsLoader, bvhNodeId);
      }
    }
  }

  /**
   * Store a completed inner node's vertex buffer to OPFS. The caller must have checked
   * that OPFS I/O is allowed and that the node is neither cached nor given up yet.
   * @param {RcsFileLoader} rcsLoader - Loader whose fragment geometry is serialized
   * @param {number} bvhNodeId - Inner BVH node id to persist
   */
  async storeInnerNodeFromLoader(rcsLoader, bvhNodeId) {
    try {
      const model = /** @type {Model} */rcsLoader.model;
      const pkg = /** @type {RcsPackage} */model.getData();
      const pointCount = pkg.getNodePointCount(bvhNodeId);
      const bvh = model.getIterator().getBVH();
      const innerFragId = bvh.nodes.getPrimStart(bvhNodeId);
      const geometry = innerFragId != null ? model.getFragmentList().getGeometry(innerFragId) : null;
      if (!geometry || !geometry.vb) {
        // Nothing to persist: an empty node has legitimately completed; a
        // point-bearing node without geometry is abandoned for caching.
        if (pointCount === 0) this.markInnerNodeCached(bvhNodeId);else
        this.giveUpInnerNodeCache(bvhNodeId);
        return;
      }

      const data = serializePointData(geometry.vb);
      const success = await this._storeCachedBlob(
        innerNodeHashKey(this.cacheId, bvhNodeId),
        getDataBucketName(this.cacheId),
        data,
        [data.buffer],
        { immediate: true }
      );

      if (!success) {
        this.giveUpInnerNodeCache(bvhNodeId);
        return;
      }

      this.markInnerNodeCached(bvhNodeId);
    } catch {
      this.giveUpInnerNodeCache(bvhNodeId);
    }
  }

  /**
   * Remove the data OPFS bucket after bootstrap blobs fail validation.
   * @returns {Promise<void>}
   */
  async _invalidateDataBucket() {
    if (!this.cacheId) return;
    await this._deleteBucket(getDataBucketName(this.cacheId), 'Failed to invalidate RCS OPFS data bucket:');
  }

  /**
   * Load raw header and octree chunk bytes from OPFS.
   *
   * @returns {Promise<{headerBytes: Uint8Array, chunk2Bytes: Uint8Array}|null>}
   */
  async loadHeaderAndOctree() {
    if (!this._isOpfsIoAllowed()) return null;
    try {
      const dataBucket = getDataBucketName(this.cacheId);

      const result = await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_GET', {
        hashKeys: [headerHashKey(this.cacheId), octreeHashKey(this.cacheId)],
        bucketNames: [dataBucket, dataBucket]
      });

      const datas = result?.result?.datas;
      if (!datas || !datas[0] || !datas[1]) {
        RcsOpfsCacheStats.cacheMisses++;
        return null;
      }

      const headerBytes = datas[0];
      const chunk2Bytes = datas[1];
      if (!validateHeaderBytes(headerBytes) || !validateOctreeChunkBytes(chunk2Bytes)) {
        RcsOpfsCacheStats.cacheMisses++;
        await this._invalidateDataBucket();
        return null;
      }

      RcsOpfsCacheStats.cacheHits += 2;
      RcsOpfsCacheStats.cacheBytesLoaded += headerBytes.byteLength + chunk2Bytes.byteLength;

      return { headerBytes, chunk2Bytes };
    } catch (e) {
      console.warn("Failed to load RCS header from OPFS cache:", e);
      return null;
    }
  }

  /**
   * Load the per-model LOD-0 source level count from OPFS, if present.
   * @returns {Promise<number|null>}
   */
  async loadDirectModeLevelsInLod0() {
    if (!this._isOpfsIoAllowed()) return null;
    try {
      const dataBucket = getDataBucketName(this.cacheId);
      const data = await RcsOpfsCacheClient._enqueueGet(levelsInLod0HashKey(this.cacheId), dataBucket);
      if (!data) {
        RcsOpfsCacheStats.cacheMisses++;
        return null;
      }
      const levels = deserializeLevelsInLod0(data);
      if (levels === null) {
        RcsOpfsCacheStats.cacheMisses++;
        return null;
      }
      RcsOpfsCacheStats.cacheHits++;
      RcsOpfsCacheStats.cacheBytesLoaded += data.byteLength;
      return levels;
    } catch (e) {
      console.warn('Failed to load RCS levelsInLod0 from OPFS cache:', e);
      RcsOpfsCacheStats.cacheMisses++;
    }
    return null;
  }

  /**
   * Persist the per-model LOD-0 source level count (fire-and-forget).
   * @param {number} directModeLevelsInLod0
   */
  storeDirectModeLevelsInLod0(directModeLevelsInLod0) {
    if (!this._isOpfsIoAllowed()) return;
    const data = serializeLevelsInLod0(directModeLevelsInLod0);
    this._storeBlobFireAndForget(
      levelsInLod0HashKey(this.cacheId),
      getDataBucketName(this.cacheId),
      data,
      'Failed to store RCS levelsInLod0 to OPFS cache:'
    );
  }

  /**
   * Store raw header and octree chunk bytes, algorithm params, and levelsInLod0 to OPFS.
   *
   * Transfers the ArrayBuffers backing `headerBytes` and `chunk2Bytes` to the fetch
   * worker; callers must not read from those views after this call returns.
   *
   * @param {Uint8Array} headerBytes - Raw RCS file header bytes
   * @param {Uint8Array} chunk2Bytes - Raw umbrella octree chunk (chunk 2) bytes
   * @param {number} directModeLevelsInLod0 - Per-model LOD-0 source level count to persist
   */
  async storeHeaderAndOctree(headerBytes, chunk2Bytes, directModeLevelsInLod0) {
    if (!this._isOpfsIoAllowed()) return;
    const dataBucket = getDataBucketName(this.cacheId);
    const paramsData = serializeCacheParams();
    const levelsData = serializeLevelsInLod0(directModeLevelsInLod0);

    try {
      await rcsWorkerPool.dispatchOpfsJob('RCS_OPFS_STORE', {
        hashKeys: [
        paramsHashKey(this.cacheId),
        headerHashKey(this.cacheId),
        octreeHashKey(this.cacheId),
        levelsInLod0HashKey(this.cacheId)],

        bucketNames: [dataBucket, dataBucket, dataBucket, dataBucket],
        datas: [paramsData, headerBytes, chunk2Bytes, levelsData]
      }, [paramsData.buffer, headerBytes.buffer, chunk2Bytes.buffer, levelsData.buffer]);
    } catch (e) {
      console.warn("Failed to store RCS header to OPFS cache:", e);
    }
  }
}